<template>
  <div class="base-input form-control" :class="classes">
    <label v-if="label" class="block text-sm font-medium text-neutral-700 dark:text-slate-300 mb-1 pointer-events-none">
      {{ label }}
      <span v-if="props.required" class="text-destructive-400">*</span>
      <span
        v-if="props.info"
        :title="props.info"
      >
        <InformationCircleIcon
          class="ml-2 h-4 w-4 text-info inline"
        />
      </span>
    </label>
    <div class="base-input__wrapper">
      <input
        ref="inputRef"
        :value="modelValue"
        v-bind="$attrs"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :readonly="readonly"
        :type="type"
        :min="min"
        @input="handleInput"
        @blur="emit('blur')"
      />
    </div>
    <p v-for="error in errors" :key="error" class="mt-1 text-sm text-destructive-600 dark:text-destructive-400">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { ref, onMounted, computed } from 'vue'

interface Props {
  modelValue: string | number | null | undefined
  label?: string
  errors?: string[]
  placeholder?: string
  autofocus?: boolean
  type?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  variant?: 'primary'
  info?: string
  insetLabel?: boolean
  min?: string | number | null | undefined
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null],
  'blur': [],
}>()

const inputRef = ref<HTMLInputElement | null>(null)

const classes = computed<string[]>(() => {
  const tmp: string[] = [];

  if ((props.errors?.length || 0) > 0) tmp.push('base-input--error')
  if (props.variant === 'primary') tmp.push('base-input--variant-primary')
  if (props.insetLabel) tmp.push('base-input--inset-label')

  return tmp;
})

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value
  
  // Konvertiere leere Strings zu null für optionale Felder
  if (value === '') {
    emit('update:modelValue', null)
    return
  }
  
  // Konvertiere Zahlen für number-type Inputs
  if (props.type === 'number' && value) {
    emit('update:modelValue', Number(value))
    return
  }
  
  emit('update:modelValue', value)
}

const focus = () => {
  inputRef.value?.focus()
}

onMounted(() => {
  if (props.autofocus) {
    focus()
  }
})

defineExpose({
  focus
})
</script>

<script lang="ts">
export default {
  name: 'BaseInput'
}
</script>

<style>
@reference "@/assets/main.css";

.base-input {
  /* @apply form-control; */
  @apply relative;
}

.base-input input {
  @apply h-full w-full px-[12px] rounded-[8px] font-['Inter'] text-neutral-900 dark:text-white bg-white dark:bg-slate-700/50 border border-neutral-200 dark:border-slate-600 disabled:bg-neutral-50 dark:disabled:bg-slate-700/30 disabled:text-neutral-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 hover:border-primary-500 dark:hover:border-primary-400 transition-colors;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.04);
}

.base-input input::placeholder {
  color: var(--Neutral-400, #9CA3AF);
}

.base-input__wrapper, .dp__input_wrap {
  @apply relative h-[40px];
}
.dp__input_wrap input {
  padding-inline: 38px;
}
.dp__input_icon {
  inset-inline-start: none;
}
.dp--clear-btn {
  inset-inline-end: none;
}

.base-input input[type="number"] {
  @apply text-right;
}

.base-input--error input {
  @apply border-red-300 focus:ring-red-500;
}

.base-input--variant-primary input {
  @apply h-10 bg-white/50 border border-white/60 rounded-lg text-slate-900 text-sm placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-[#329C9C] focus:border-transparent backdrop-blur-sm;
}

.base-input--inset-label label {
  background: linear-gradient(0deg, var(--color-white) 0%, var(--color-white) 100%);
  left: 10px;
  padding: 0 2px;
  position: absolute;
  top: -10px;
  z-index: 2;
  white-space: nowrap;
}

:root[class~="dark"] input {
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
}

:root[class~="dark"] input::placeholder {
  color: var(--Slate-500, #64748b);
}

:root[class~="dark"] input:focus {
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1), 0 0 0 2px rgba(59, 130, 246, 0.1);
}
</style>
